import {CheckCircleIcon, CircleSlashIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useMemo} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS, VALUES} from '../constants'

import {createIssueEventExternalUrl} from '../utils/urls'
import type {ClosedEvent$key} from './__generated__/ClosedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {getStateQuery} from '../utils/get-state-query'
import {commitHovercardPath, issueLinkedPullRequestHovercardPath} from '@github-ui/paths'

type ClosedEventProps = {
  queryRef: ClosedEvent$key
  issueUrl: string
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function ClosedEvent({
  queryRef,
  issueUrl,
  timelineEventBaseUrl,
  highlightedEventId,
  refAttribute,
}: ClosedEventProps): JSX.Element {
  const {actor, createdAt, stateReason, databaseId, closer} = useFragment(
    graphql`
      fragment ClosedEvent on ClosedEvent {
        databaseId
        createdAt
        stateReason
        closer {
          __typename
          ... on PullRequest {
            url
            number
            repository {
              name
              owner {
                login
              }
            }
          }
          ... on Commit {
            url
            abbreviatedOid
            repository {
              name
              owner {
                login
              }
            }
          }
        }
        actor {
          ...EventActor
        }
      }
    `,
    queryRef,
  )

  const highlighted = String(databaseId) === highlightedEventId
  const {stateChangeQuery, stateReasonString} = getStateQuery(stateReason)
  const backgroundColor = stateReason === VALUES.issueStateReason.NOT_PLANNED ? 'fg.muted' : 'done.fg'
  const icon = stateReason === VALUES.issueStateReason.NOT_PLANNED ? CircleSlashIcon : CheckCircleIcon

  const queryUrl = useMemo(() => {
    return `${timelineEventBaseUrl}?q=${stateReason && encodeURIComponent(stateChangeQuery)}`
  }, [stateChangeQuery, stateReason, timelineEventBaseUrl])

  const closedByCommit = closer?.__typename === 'Commit'
  const closerNumber = closer && closer.__typename === 'PullRequest' ? closer.number : undefined
  const closerPullRequestUrl = closer && closer.__typename === 'PullRequest' ? closer.url : undefined
  const closerCommitUrl = closedByCommit ? closer.url : undefined

  const dataHovercardUrl = closer
    ? closer.__typename === 'Commit'
      ? getDataHovercardUrlForCommit(closer.repository.owner.login, closer.repository.name, closer.abbreviatedOid)
      : closer.__typename === 'PullRequest'
        ? getDataHovercardUrlForPullRequest(closer.repository.owner.login, closer.repository.name, closer.number)
        : null
    : null

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={icon}
      iconColoring={{backgroundColor, color: 'white'}}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.closedThis} `}
        {stateReason && (
          <>
            {`${LABELS.timeline.as} `}
            <Link href={queryUrl} sx={{color: 'fg.default', mr: 1}} muted>
              {stateReasonString}
            </Link>
          </>
        )}
        {closer && (
          <>
            {`${LABELS.timeline.in} `}
            <Link
              href={closedByCommit ? closerCommitUrl : closerPullRequestUrl}
              target="_blank"
              sx={{color: 'accent.fg', mr: 1}}
              data-hovercard-url={dataHovercardUrl}
            >
              {closedByCommit ? `${closer.abbreviatedOid}` : `#${closerNumber}`}
            </Link>
          </>
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

function getDataHovercardUrlForCommit(owner: string, repo: string, commitHash: string): string {
  return commitHovercardPath({owner, repo, commitish: commitHash})
}

function getDataHovercardUrlForPullRequest(owner: string, repo: string, pullRequestNumber: number): string {
  return issueLinkedPullRequestHovercardPath({
    owner,
    repo,
    pullRequestNumber,
  })
}

try{ ClosedEvent.displayName ||= 'ClosedEvent' } catch {}